import { useEffect, useMemo, useRef, useState } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { Outlet, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { businessesCollection, productsCollection, storeInventoryCollection, tablesCollection, eq } from "@cliqsuite/db";
import { Sidebar } from "./Sidebar";
import { VerificationBanner } from "./VerificationBanner";
import { pageMeta, findNavItem, isNavItemUnlocked } from "./navConfig";
import { useAuth } from "../../lib/auth";
import { useStoreScope } from "../../lib/storeScope";
import { PeriodPicker } from "./PeriodPicker";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useInstalledModules } from "../../lib/useInstalledModules";
import { DESKTOP_ACTIONS_ID, PAGE_BANNER_ID } from "./PageHeaderActions";
import { PhoneNav } from "./PhoneNav";
import { usePermissions } from "../../lib/permissions";
import { timeAgo } from "../../lib/format";

/** "Today, Tue 27 Oct": Overview's day. */
function todayLabel(): string {
  return `Today, ${new Date().toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}`;
}

type NotificationKind = "stock" | "summary";

interface NotificationItem {
  id: string;
  icon: string;
  /** the icon's colour (canvas v2-om-alerts): out of stock coral, low amber, summary grey */
  tone: "out" | "low" | "summary";
  title: string;
  body: string;
  time: string;
  unread: boolean;
  kind: NotificationKind;
  /** the page a tap opens */
  to: string;
}

const NOTIF_FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "New" },
  { key: "stock", label: "Stock" },
  { key: "summary", label: "Daily summary" },
] as const;
type NotifFilterKey = (typeof NOTIF_FILTERS)[number]["key"];

interface StockAlert {
  productId: string;
  name: string;
  qty: number;
  par: number;
  isOut: boolean;
  isLow: boolean;
  updatedAt: string | null;
}

/** Same low/out-of-stock condition as inventory/StockLevelsTab.tsx. Shared
 * by the notification list itself and the recovery-detection effect below
 * so the two can't drift out of sync with each other. */
function computeStockAlerts(
  products: ReadonlyArray<{ id: string; name: string; is_inventory_item: boolean }> | undefined,
  invRows: ReadonlyArray<{ product_id: string; variant_id: string | null; quantity: number; low_stock_threshold: number; is_out_of_stock: boolean; updated_at: string }> | undefined,
): StockAlert[] {
  const invByProduct = new Map((invRows ?? []).filter((r) => !r.variant_id).map((r) => [r.product_id, r]));
  const alerts: StockAlert[] = [];
  for (const p of products ?? []) {
    if (!p.is_inventory_item) continue;
    const inv = invByProduct.get(p.id);
    const qty = inv?.quantity ?? 0;
    const par = inv?.low_stock_threshold ?? 0;
    const isOut = !!(inv?.is_out_of_stock || qty === 0);
    const isLow = isOut || (par > 0 && qty <= par);
    alerts.push({ productId: p.id, name: p.name, qty, par, isOut, isLow, updatedAt: inv?.updated_at ?? null });
  }
  return alerts;
}

export function AppShell() {
  const { storeId, store, stores, setStoreId } = useStoreScope();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isDesktop = useIsDesktop();
  const [notiOpen, setNotiOpen] = useState(false);
  const [storePickOpen, setStorePickOpen] = useState(false);
  const [notifFilter, setNotifFilter] = useState<NotifFilterKey>("all");
  // No platform.notifications table exists yet, so there's nothing to mark
  // read/dismissed server-side — both are just local Sets, scoped to this
  // session (same as the mockup's own in-memory state). "summary" (daily
  // recap) has no real data source yet either — only "stock" (low/out of
  // stock, current store) is wired for this first pass.
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const { data: invProducts } = useLiveQuery(
    (q) =>
      storeId
        ? q.from({ p: productsCollection }).where(({ p }) => eq(p.store_id, storeId))
        : q.from({ p: productsCollection }).where(({ p }) => eq(p.id, "__none__")),
    [storeId],
  );
  const { data: invRows } = useLiveQuery(
    (q) =>
      storeId
        ? q.from({ inv: storeInventoryCollection }).where(({ inv }) => eq(inv.store_id, storeId))
        : q.from({ inv: storeInventoryCollection }).where(({ inv }) => eq(inv.id, "__none__")),
    [storeId],
  );
  const stockAlerts = useMemo(() => computeStockAlerts(invProducts, invRows), [invProducts, invRows]);

  // Dismissing a low-stock alert only hides that occurrence, not the
  // product forever: once it's restocked back above threshold (isLow flips
  // false), its dismissal clears here, so if it dips low again later it
  // shows up fresh instead of staying silently hidden. Same edge-detection
  // shape as PosApp.tsx's kitchen-ready effect — a ref of "last known
  // status" diffed against the current pass.
  const prevLowRef = useRef<Map<string, boolean> | null>(null);
  useEffect(() => {
    const current = new Map(stockAlerts.map((a) => [a.productId, a.isLow]));
    const prev = prevLowRef.current;
    prevLowRef.current = current;
    if (prev === null) return; // first load — nothing recovered yet, nothing to clear

    const recoveredIds: string[] = [];
    current.forEach((isLow, productId) => {
      if (prev.get(productId) && !isLow) recoveredIds.push(`low-stock:${productId}`);
    });
    if (recoveredIds.length === 0) return;
    setDismissedIds((prevSet) => {
      const next = new Set(prevSet);
      recoveredIds.forEach((id) => next.delete(id));
      return next;
    });
  }, [stockAlerts]);

  const notifications = useMemo(() => {
    const items: NotificationItem[] = [];
    for (const a of stockAlerts) {
      if (!a.isLow) continue;
      const id = `low-stock:${a.productId}`;
      if (dismissedIds.has(id)) continue;
      items.push({
        id,
        icon: a.isOut ? "remove_shopping_cart" : "warning",
        tone: a.isOut ? "out" : "low",
        title: a.isOut ? `${a.name} is out of stock` : `${a.name} is running low`,
        body: `${a.qty} on hand, reorder at ${a.par}`,
        // when its stock last changed: the nearest thing to when it went low
        time: a.updatedAt ? timeAgo(a.updatedAt).replace(" ago", "") : "",
        unread: !readIds.has(id),
        kind: "stock",
        to: "/inventory",
      });
    }
    return items;
  }, [stockAlerts, dismissedIds, readIds]);
  const filteredNotifications = notifications.filter((n) => {
    if (notifFilter === "unread") return n.unread;
    if (notifFilter === "stock" || notifFilter === "summary") return n.kind === notifFilter;
    return true;
  });
  const markRead = (id: string) => setReadIds((prev) => new Set(prev).add(id));
  const markAllRead = () => setReadIds(new Set(notifications.map((n) => n.id)));
  const clearRead = () => setDismissedIds((prev) => new Set([...prev, ...notifications.filter((n) => !n.unread).map((n) => n.id)]));
  const notifCount = (key: NotifFilterKey) =>
    key === "all" ? notifications.length : key === "unread" ? notifications.filter((n) => n.unread).length : notifications.filter((n) => n.kind === key).length;
  const navigate = useNavigate();
  const openNotification = (n: NotificationItem) => {
    markRead(n.id);
    setNotiOpen(false);
    navigate({ to: n.to });
  };
  const { modules: installedModules, isLoading: modulesLoading } = useInstalledModules();
  const navItem = findNavItem(pathname);
  const { can, isReady: permissionsReady } = usePermissions();
  const pageUnlocked = modulesLoading || !navItem || isNavItemUnlocked(navItem, installedModules);
  const pageAllowed = !permissionsReady || can(navItem?.permission);
  const unreadCount = notifications.filter((n) => n.unread).length;
  // Overview is today only (canvas v2-o-overview-today-proposed); the period key is for Reports and Bills.
  const showRange = pathname === "/sales" || pathname === "/analytics" || pathname === "/bills";
  const meta = usePageMeta(pathname);
  // Phone: the page itself scrolls (as the guest app), so a new page starts at its top.
  useEffect(() => {
    if (!isDesktop) window.scrollTo(0, 0);
  }, [pathname, isDesktop]);

  // Desktop: the title, the range and the alerts key head the sheet. Phone (canvas v2-om-*, 2026-10-18): the drawer key, the
  // title and the alerts key sit on the coral frame above the sheet, and the range is the first thing in the sheet.
  const headText = (
    <div className="o-head__text">
      {pathname.startsWith("/stores/") && (
        <Link to="/stores" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5, fontWeight: 800, color: "var(--text2)", textDecoration: "none", marginBottom: 8 }}>
          <span className="ms" aria-hidden="true" style={{ fontSize: 18 }}>arrow_back</span>
          Stores
        </Link>
      )}
      <h1>{meta.title}</h1>
      {meta.sub && <div>{meta.sub}</div>}
    </div>
  );
  const headNoti = (
    <div className="o-head__noti">
      <button
        type="button"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
        aria-expanded={notiOpen}
        className="o-head__btn"
        onClick={() => setNotiOpen((v) => !v)}
      >
        <span className="ms" aria-hidden="true">notifications</span>
        {unreadCount > 0 && <span className="o-head__dot" />}
      </button>
      {notiOpen && (
        <>
          <div className="o-noti__shade" onClick={() => setNotiOpen(false)} />
          {/* canvas v2-om-alerts: chips to filter, a row per alert that opens what it is about; on a phone a sheet rising from the bell */}
          <div className="o-popover o-popover--light o-noti" role="dialog" aria-label="Alerts">
            {!isDesktop && <span className="o-qsheet__grab" />}
            <div className="o-noti__head">
              <b>
                Alerts
                {unreadCount > 0 && <small>{unreadCount} new</small>}
              </b>
              {unreadCount > 0 && (
                <button type="button" className="o-link" onClick={markAllRead}>
                  Mark all read
                </button>
              )}
              {notifications.length > unreadCount && (
                <button type="button" className="o-link o-noti__clear" onClick={clearRead}>
                  Clear read
                </button>
              )}
            </div>
            <div className="o-chips o-chips--scroll o-noti__chips" role="radiogroup" aria-label="Show">
              {/* no daily summary is sent yet, so its chip waits until there is one */}
              {NOTIF_FILTERS.filter((f) => f.key !== "summary" || notifCount("summary") > 0).map((f) => (
                <button type="button" role="radio" aria-checked={notifFilter === f.key} key={f.key} onClick={() => setNotifFilter(f.key)}>
                  {f.label} <i>{notifCount(f.key)}</i>
                </button>
              ))}
            </div>
            {filteredNotifications.length === 0 ? (
              <div className="o-empty" style={{ padding: "28px 20px" }}>
                <span className="ms" aria-hidden="true">notifications_off</span>
                <strong>No alerts</strong>
              </div>
            ) : (
              <div className="o-noti__list">
                {filteredNotifications.map((n) => (
                  <button type="button" key={n.id} className={`o-noti__row${n.unread ? " is-new" : ""}`} onClick={() => openNotification(n)}>
                    <span className={`o-ico o-noti__ico--${n.tone}`}>
                      <span className="ms" aria-hidden="true">{n.icon}</span>
                    </span>
                    <span className="o-noti__main">
                      <b>{n.title}</b>
                      <small>{n.body}</small>
                    </span>
                    <span className="o-noti__end">
                      {n.time && <span className="o-noti__time">{n.time}</span>}
                      {n.unread && <i className="o-noti__dot" aria-label="New" />}
                    </span>
                    <span className="ms" aria-hidden="true">chevron_right</span>
                  </button>
                ))}
              </div>
            )}
            <p className="o-noti__hint">Low stock clears itself once it is restocked.</p>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className={isDesktop ? "o-shell" : "o-shell o-shell--phone"}>
      {/* phone: the bottom bar (PhoneNav) takes the sidebar's place */}
      {isDesktop && <Sidebar mobileOpen={false} onCloseMobile={() => {}} />}
      {!isDesktop && (
        // Phone (canvas v2-om-nav-*): no drawer key; the bottom bar has the pages. The store under the title switches store.
        <header className="o-phead o-phead--nav">
          <div className="o-head__text">
            {pathname.startsWith("/stores/") && (
              <Link to="/stores" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13.5, fontWeight: 800, textDecoration: "none", marginBottom: 8 }}>
                <span className="ms" aria-hidden="true" style={{ fontSize: 18 }}>arrow_back</span>
                Stores
              </Link>
            )}
            <h1>{meta.title}</h1>
            {store && (
              <button type="button" className="o-phead__store" aria-label="Switch store" aria-expanded={storePickOpen} onClick={() => stores.length > 1 && setStorePickOpen((v) => !v)}>
                {store.name}
                {stores.length > 1 && <span className="ms" aria-hidden="true">expand_more</span>}
              </button>
            )}
            {storePickOpen && (
              <>
                <div onClick={() => setStorePickOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 65 }} />
                <div className="o-popover o-popover--light" style={{ top: "calc(100% - 6px)", left: 16, minWidth: 220 }}>
                  {stores.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      className={`o-popover__item${s.id === store?.id ? " is-active" : ""}`}
                      onClick={() => {
                        setStoreId(s.id);
                        setStorePickOpen(false);
                      }}
                    >
                      <span style={{ flex: 1 }}>{s.name}</span>
                      {s.id === store?.id && <span className="ms" aria-hidden="true">check</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </header>
      )}
      {!isDesktop && <PhoneNav />}
      {/* phone: the bell sits above the bottom bar and its list rises from there (portal.css .o-pbell) */}
      {!isDesktop && <div className="o-pbell">{headNoti}</div>}
      <main className="o-sheet">
        <div className="o-page">
          <VerificationBanner />
          <div id={PAGE_BANNER_ID} />
          {isDesktop ? (
            <header className="o-head">
              {headText}
              <div className="o-head__actions">
                {showRange && <PeriodPicker phone={false} />}
                {isDesktop && <div id={DESKTOP_ACTIONS_ID} className="o-head__slot" />}
              </div>
              {headNoti}
            </header>
          ) : showRange ? (
            <div className="o-phead__range">
              <PeriodPicker phone />
            </div>
          ) : (
            pathname === "/" && <div className="o-phead__date">{todayLabel()}</div>
          )}

          {pageUnlocked && pageAllowed ? (
            <Outlet />
          ) : !pageAllowed ? (
            <div className="o-empty o-empty--page">
              <span className="ms" aria-hidden="true">lock</span>
              <strong>You don't have access to this page</strong>
              <p>Ask the business owner to update your role permissions.</p>
            </div>
          ) : (
            <div className="o-empty o-empty--page">
              <span className="ms" aria-hidden="true">widgets</span>
              <strong>Module not installed</strong>
              <p>{meta.title} needs a module that isn't switched on for this business. Turn it on in Modules to open this page.</p>
              <Link to="/modules" className="o-btn o-btn--ink" style={{ marginTop: 8 }}>
                <span className="ms" aria-hidden="true">widgets</span>
                Go to Modules
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

/** The canvas greets the owner on Overview and names the store on per-store pages. */
function usePageMeta(pathname: string): { title: string; sub: string } {
  const { user } = useAuth();
  const { store, stores } = useStoreScope();
  const businessId = user?.business_id;
  const { data: businesses } = useLiveQuery(
    (q) =>
      businessId
        ? q.from({ b: businessesCollection }).where(({ b }) => eq(b.id, businessId))
        : q.from({ b: businessesCollection }).where(({ b }) => eq(b.id, "__none__")),
    [businessId],
  );
  const business = businesses?.[0]?.name;
  const { data: tables } = useLiveQuery(
    (q) => q.from({ t: tablesCollection }).where(({ t }) => eq(t.store_id, pathname === "/tables" && store ? store.id : "__none__")),
    [pathname, store?.id],
  );
  if (pathname.startsWith("/stores/")) {
    const page = stores.find((s) => s.id === pathname.slice("/stores/".length));
    return { title: page?.name ?? "Store", sub: page?.address ?? "" };
  }
  const meta = pageMeta[pathname] ?? { title: "CliqSuite", sub: "" };
  if (pathname === "/") {
    const h = new Date().getHours();
    const part = h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
    const first = user?.name?.split(" ")[0];
    return { title: first ? `Good ${part}, ${first}` : `Good ${part}`, sub: `${todayLabel()}${business ? `, across ${business}` : ""}` };
  }
  if (pathname === "/sales" && store) return { ...meta, sub: `Sales for ${store.name}` };
  if (pathname === "/tables" && tables?.length) {
    const live = tables.filter((t) => t.is_active);
    return { ...meta, sub: `${live.reduce((n, t) => n + t.seats, 0)} seats across ${live.length} table${live.length === 1 ? "" : "s"}, all live on the register` };
  }
  if (pathname === "/devices" && store) return { ...meta, sub: `Registers, kitchen screens and terminals in ${store.name}` };
  if (pathname === "/payroll") return { ...meta, sub: new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" }) };
  if (pathname === "/inventory" && store) return { ...meta, sub: `Catalog, stock and transfers for ${store.name}` };
  return meta;
}
